import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { useScreenSize } from 'shared/lib/screen/useScreenSize';
import { StorageKeys } from 'shared/lib/storage/storageService';

export const SIDEBAR_WIDTH = 216;
export const SIDEBAR_RAIL_WIDTH = 56;

export const SIDEBAR_WIDTH_VAR = '--sidebar-width';
export const SIDEBAR_TRANSITION = { duration: 0.2, ease: 'easeOut' } as const;

type SidebarState = {
  isPinned: boolean | null;
  isHovered: boolean;
  setPinned: (isPinned: boolean) => void;
  setHovered: (isHovered: boolean) => void;
};

export const useSidebarStore = create<SidebarState>()(
  persist(
    (set) => ({
      isPinned: null,
      isHovered: false,
      setPinned: (isPinned) => set({ isPinned }),
      setHovered: (isHovered) => set({ isHovered }),
    }),
    {
      name: StorageKeys.SIDEBAR_STATE,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ isPinned: state.isPinned }),
    },
  ),
);

export const useIsSidebarPinned = () => {
  const isPinned = useSidebarStore((state) => state.isPinned);
  const { isWideDesktop } = useScreenSize();

  return isPinned ?? isWideDesktop;
};

export const useIsSidebarExpanded = () => {
  const isHovered = useSidebarStore((state) => state.isHovered);

  return useIsSidebarPinned() || isHovered;
};

/** Space the sidebar takes in the layout. Hover-expanding widens it, so the content moves with it. */
export const useSidebarWidth = () => (useIsSidebarExpanded() ? SIDEBAR_WIDTH : SIDEBAR_RAIL_WIDTH);
